import { normalizePath } from '../../../../utils/path';

export async function execute(args, context) {
  const { dbRef, saveFile, onFileChanged } = context;
  const { path, find, replace } = args;

  const normalizedPath = normalizePath(path);

  try {
    const content = await dbRef.current.get('files', normalizedPath);

    if (!content) {
      return {
        success: false,
        error: `File not found: ${normalizedPath}`
      };
    }

    // Normalize line endings
    const normalizedContent = content.replace(/\r\n/g, '\n');
    let normalizedFind = find.replace(/\r\n/g, '\n');

    // Try exact match first
    if (!normalizedContent.includes(normalizedFind)) {
      // Try normalizing & to &amp; for HTML
      normalizedFind = normalizedFind.replace(/&(?!(amp|lt|gt|nbsp|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;');
    }

    // Still no match? Try whitespace-normalized matching
    if (!normalizedContent.includes(normalizedFind)) {
      // Collapse all whitespace runs to single space, trim lines
      const collapseWs = (s) => s.split('\n').map(line => line.trim()).join('\n');
      const collapsedContent = collapseWs(normalizedContent);
      const collapsedFind = collapseWs(find.replace(/\r\n/g, '\n'));

      if (collapsedContent.includes(collapsedFind)) {
        // Find the actual text in original content by matching line by line
        const findLines = collapsedFind.split('\n').filter(l => l);
        const contentLines = normalizedContent.split('\n');

        for (let i = 0; i <= contentLines.length - findLines.length; i++) {
          let match = true;
          for (let j = 0; j < findLines.length; j++) {
            if (contentLines[i + j].trim() !== findLines[j]) {
              match = false;
              break;
            }
          }
          if (match) {
            // Found it - extract actual text with original whitespace
            normalizedFind = contentLines.slice(i, i + findLines.length).join('\n');
            break;
          }
        }
      }
    }

    // Check if find text exists
    if (!normalizedContent.includes(normalizedFind)) {
      const lines = content.split('\n');
      const findFirstWord = find.trim().split(/\s+/)[0];
      const similarLines = lines
        .map((line, i) => ({ line: line.trim(), num: i + 1 }))
        .filter(({ line }) => line.includes(findFirstWord))
        .slice(0, 5);

      let hint = 'Text not found in file.';
      if (similarLines.length > 0) {
        hint += `\n\nSimilar content at lines ${similarLines.map(s => s.num).join(', ')}:`;
        hint += '\n' + similarLines.map(s => `Line ${s.num}: ${s.line.slice(0, 100)}`).join('\n');
        hint += '\n\nUse the EXACT text shown above for edit_file.';
      }

      return {
        success: false,
        error: hint,
        searchedFor: find.length > 100 ? find.slice(0, 100) + '...' : find
      };
    }

    // Check for multiple matches
    const matchCount = normalizedContent.split(normalizedFind).length - 1;
    if (matchCount > 1) {
      return {
        success: false,
        error: `Found ${matchCount} matches. Include more context to match uniquely.`,
        matchCount
      };
    }

    // Normalize replace - match indentation of first line of find
    let normalizedReplace = replace.replace(/\r\n/g, '\n');
    const findFirstLineIndent = normalizedFind.match(/^(\s*)/)?.[1] || '';
    const replaceFirstLineIndent = normalizedReplace.match(/^(\s*)/)?.[1] || '';

    if (findFirstLineIndent !== replaceFirstLineIndent) {
      // Adjust replacement indentation to match original
      const replaceLines = normalizedReplace.split('\n');
      normalizedReplace = replaceLines.map((line, i) => {
        if (i === 0) return findFirstLineIndent + line.trimStart();
        // Preserve relative indentation for subsequent lines
        const lineIndent = line.match(/^(\s*)/)?.[1] || '';
        const relativeIndent = lineIndent.length - replaceFirstLineIndent.length;
        const newIndent = findFirstLineIndent + ' '.repeat(Math.max(0, relativeIndent));
        return newIndent + line.trimStart();
      }).join('\n');
    }

    // Handle &amp; encoding if needed
    if (normalizedFind.includes('&amp;') && !normalizedReplace.includes('&amp;')) {
      normalizedReplace = normalizedReplace.replace(/&(?!(amp|lt|gt|nbsp|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;');
    }

    // Apply replacement
    const newContent = normalizedContent.replace(normalizedFind, () => normalizedReplace);

    // Save
    await saveFile(normalizedPath, newContent);

    // Track changed files
    const changedFiles = JSON.parse(sessionStorage.getItem('changedFiles')) || [];
    if (!changedFiles.includes(normalizedPath)) {
      changedFiles.push(normalizedPath);
      sessionStorage.setItem('changedFiles', JSON.stringify(changedFiles));
    }

    // Notify about change
    if (onFileChanged) {
      await onFileChanged(normalizedPath, newContent);
    }

    return {
      success: true,
      path: normalizedPath,
      message: `Edit applied to ${normalizedPath}`,
      diff: {
        removed: find.length,
        added: replace.length
      }
    };
  } catch (err) {
    return {
      success: false,
      error: `Failed to edit file: ${err.message}`
    };
  }
}
